import { WORKSPACE_BACKGROUND_PICKER_DISMISSED_EVENT } from '../../lib/workspaceBackgroundPickerDismissal';

import type { ChatItem } from '../core/types';
import { type CurrentEndOfMessagesHeightContext, isEndOfMessagesItem } from '../echo/itemHeightEstimator';
import type { FeedItemKey } from '../engine';

type EndOfMessagesContext = CurrentEndOfMessagesHeightContext['current'];

/** Stable dependency key for every route-owned input to terminal-row geometry. */
export function endOfMessagesHeightContextKey(context: EndOfMessagesContext): string {
  return JSON.stringify([
    context.hasCurrentChannel,
    context.isMainOrSharedChannel,
    context.isOpenBreakout,
    context.isOnboardingAdmin,
    context.onboardingCardIds,
    context.profileId,
    context.workspaceId,
  ]);
}

/** Invalidates only context-dependent terminal rows from a loaded controller snapshot. */
export function invalidateEndOfMessagesHeights(items: readonly ChatItem[], invalidateItemHeight: (key: FeedItemKey) => void): void {
  for (const item of items) {
    if (isEndOfMessagesItem(item)) invalidateItemHeight(item.itemKey);
  }
}

/** Defers contextual reflow until the target conversation owns the installed window. */
export function scheduleEndOfMessagesHeightInvalidation(
  ready: Promise<void>,
  isCurrentConversation: () => boolean,
  invalidate: () => void,
): () => void {
  let cancelled = false;
  void ready.then(
    () => {
      if (!cancelled && isCurrentConversation()) invalidate();
    },
    () => {
      // Session lifecycle owns failure reporting; a failed switch must not
      // apply the next route's terminal geometry to the previous window.
    },
  );
  return () => {
    cancelled = true;
  };
}

/** Connects background-picker dismissal to the same terminal-row invalidation path. */
export function subscribeEndOfMessagesHeightDismissal(invalidate: () => void, target: EventTarget = window): () => void {
  target.addEventListener(WORKSPACE_BACKGROUND_PICKER_DISMISSED_EVENT, invalidate);
  return () => target.removeEventListener(WORKSPACE_BACKGROUND_PICKER_DISMISSED_EVENT, invalidate);
}
